/**
 * v2 네이티브 문서 예시 — 상담 현황 리뷰.
 *
 * 이 화면이 예시로 선택된 이유: 현재 프로덕션에서 **커스텀 React 컴포넌트**
 * (`CounselReviewScreen.tsx`) 로 구현되어 있어 에디터로 손댈 수 없는 대표 사례이기
 * 때문이다. v1 FlowDocument 는 "질문을 하나씩 묻는" 형태만 표현할 수 있어서 이런
 * 요약/배치 화면을 담지 못한다.
 *
 * **기존 화면을 그대로 재현하는 것이 목표다.** 카드 구성·아이콘·라벨·Tag 톤·"미입력"
 * 대체 문구까지 CounselReviewScreen 을 따른다. 문서로 조립했을 때 다르게 보이면
 * 이관이 "다시 만들기"가 되어버린다.
 *
 * v2 가 이를 담기 위해 필요로 한 것은 네 가지뿐이다:
 *   - 임의 중첩 레이아웃 트리 (`children`)
 *   - 이름 있는 슬롯 (`slots`) — 카드 헤더 우측, 하단 sticky CTA
 *   - 목록 반복 (`repeat`)
 *   - 조건부 노출 (`visibleWhen`)
 */
import type { AssetDocument, DataSource } from "@miso/design-system";

/** 호스트가 등록해야 하는 이름. 상담 → 이 화면이 읽는 값 묶음. */
export const REVIEW_REFS = {
  model: "review.model",
} as const;

/**
 * 상담 하나를 이 화면의 어휘로 편다.
 *
 * `spread` 인 이유: 카드마다 `movingDate`·`departure.address` 처럼 자기 이름으로 값을
 * 읽는다. 한 겹 감싸면 문서 전체를 `model.` 접두어로 고쳐야 하고, 그러면 표본값으로
 * 화면만 띄워 보는 일(에디터 미리보기)이 안 된다.
 */
export const REVIEW_DATA: DataSource[] = [
  { id: "counsel", source: "counsel.snapshot" },
  {
    id: "_review",
    derive: REVIEW_REFS.model,
    args: { counsel: { $var: "counsel" } },
    spread: true,
  },
];

const doc: AssetDocument = {
  id: "counsel.review",
  schemaVersion: 2,
  initial: "review",
  data: REVIEW_DATA,
  steps: {
    review: {
      kind: "screen",
      layout: {
        type: "Screen",
        props: { title: "상담 현황 리뷰" },
        slots: {
          footer: [
            {
              type: "CtaButton",
              props: {
                label: "추가공간 촬영하기",
                busy: { $var: "proceeding" },
              },
              on: {
                onPress: [
                  { do: "setValues", values: { proceeding: true } },
                  /*
                   * 리뷰를 지나면 intake 세션이 닫힌다. 이게 "진행하기 7단계가 끝났다"
                   * 는 서버 쪽 표식이라, 이걸 부르지 않으면 다음 판정이 다시 리뷰를
                   * 띄운다.
                   */
                  { do: "callAction", name: "completeIntakeSession" },
                  { do: "submit" },
                ],
              },
            },
          ],
        },
        children: [
          // ── 1) 이사 개요 ──────────────────────────────────────────────────
          {
            type: "Card",
            props: { icon: "📅", title: "이사 개요" },
            slots: {
              trailing: [
                {
                  type: "TextAction",
                  props: { label: "수정" },
                  on: {
                    onPress: [
                      {
                        do: "callAction",
                        name: "review.edit",
                        args: { section: "date" },
                      },
                    ],
                  },
                },
              ],
            },
            children: [
              {
                type: "InfoRow",
                props: {
                  label: "이사일",
                  value: { $var: "movingDate" },
                  emphasize: true,
                },
              },
            ],
          },

          // ── 2) 이사 유형 ──────────────────────────────────────────────────
          {
            type: "Card",
            props: { icon: "🏷️", title: "이사 유형" },
            slots: {
              trailing: [
                {
                  type: "Tag",
                  props: { label: "기록됨", variant: "info" },
                  visibleWhen: { not: { isEmpty: { var: "movingTypeSummary" } } },
                },
                {
                  type: "Tag",
                  props: { label: "미입력", variant: "warn" },
                  visibleWhen: { isEmpty: { var: "movingTypeSummary" } },
                },
                {
                  type: "TextAction",
                  props: { label: "수정" },
                  visibleWhen: { not: { isEmpty: { var: "movingTypeSummary" } } },
                  on: {
                    onPress: [
                      {
                        do: "callAction",
                        name: "review.edit",
                        args: { section: "movingType" },
                      },
                    ],
                  },
                },
              ],
            },
            children: [
              {
                type: "Group",
                props: { padded: true, tight: true },
                children: [
                  {
                    type: "Highlight",
                    props: { text: { $var: "movingTypeSummary" } },
                    visibleWhen: { not: { isEmpty: { var: "movingTypeSummary" } } },
                  },
                  {
                    type: "Text",
                    props: {
                      text: "이사 유형이 아직 기록되지 않았어요.",
                      tone: "muted",
                    },
                    visibleWhen: { isEmpty: { var: "movingTypeSummary" } },
                  },
                ],
              },
            ],
          },

          // ── 3) 출발지 / 도착지 ────────────────────────────────────────────
          {
            type: "Card",
            props: { icon: "📍", title: "출발지 / 도착지" },
            slots: {
              trailing: [
                {
                  type: "TextAction",
                  props: { label: "수정" },
                  on: {
                    onPress: [
                      {
                        do: "callAction",
                        name: "review.edit",
                        args: { section: "departure" },
                      },
                    ],
                  },
                },
              ],
            },
            children: [
              { type: "SubHeader", props: { text: "출발지" } },
              {
                type: "InfoRow",
                props: { label: "주소", value: { $var: "departure.address" } },
              },
              {
                type: "InfoRow",
                props: {
                  label: "운반방식",
                  value: { $var: "departure.movingMethod" },
                },
              },

              { type: "SubHeader", props: { text: "도착지", separated: true } },
              {
                type: "InfoRow",
                props: { label: "주소", value: { $var: "arrival.address" } },
              },
              {
                type: "InfoRow",
                props: { label: "평수", value: { $var: "arrival.supplyArea" } },
              },
              {
                type: "InfoRow",
                props: {
                  label: "운반방식",
                  value: { $var: "arrival.movingMethod" },
                },
              },

              { type: "SubHeader", props: { text: "포장", separated: true } },
              {
                type: "InfoRow",
                props: { label: "포장방식", value: { $var: "packing" } },
              },
            ],
          },

          // ── 4) 방별 짐 & 영상 ─────────────────────────────────────────────
          {
            type: "Card",
            props: { icon: "🛋️", title: "방별 짐 & 영상" },
            slots: {
              trailing: [
                {
                  type: "Tag",
                  props: {
                    label: "{{roomsDone}}/{{roomsTotal}} 완료",
                    variant: { $var: "roomsAllDoneVariant" },
                  },
                },
              ],
            },
            children: [
              {
                type: "Group",
                props: { padded: true },
                children: [
                  {
                    type: "ListRow",
                    props: { label: { $var: "room.label" } },
                    repeat: { for: { $var: "rooms" }, as: "room", key: "id" },
                    slots: {
                      trailing: [
                        {
                          type: "Group",
                          props: { direction: "row" },
                          children: [
                            {
                              type: "Tag",
                              props: {
                                label: "짐 {{room.itemCount}}개",
                                variant: "neutral",
                              },
                              visibleWhen: {
                                ">": [{ var: "room.itemCount" }, 0],
                              },
                            },
                            {
                              type: "Tag",
                              props: { label: "짐 없음", variant: "warn" },
                              visibleWhen: {
                                "==": [{ var: "room.itemCount" }, 0],
                              },
                            },
                            {
                              type: "Tag",
                              props: { label: "영상", variant: "success" },
                              visibleWhen: {
                                "==": [{ var: "room.hasVideo" }, true],
                              },
                            },
                            {
                              type: "Tag",
                              props: { label: "미촬영", variant: "warn" },
                              visibleWhen: {
                                "!=": [{ var: "room.hasVideo" }, true],
                              },
                            },
                            {
                              type: "Tag",
                              props: { label: "완료", variant: "success" },
                              visibleWhen: {
                                and: [
                                  { "==": [{ var: "room.hasVideo" }, true] },
                                  { ">": [{ var: "room.itemCount" }, 0] },
                                ],
                              },
                            },
                            {
                              type: "Tag",
                              props: { label: "미완료", variant: "warn" },
                              visibleWhen: {
                                not: {
                                  and: [
                                    { "==": [{ var: "room.hasVideo" }, true] },
                                    { ">": [{ var: "room.itemCount" }, 0] },
                                  ],
                                },
                              },
                            },
                            {
                              type: "TextAction",
                              props: { label: "수정" },
                              on: {
                                onPress: [
                                  {
                                    do: "openSheet",
                                    sheet: "roomDetail",
                                    args: {
                                      roomLabel: { $var: "room.label" },
                                      roomItemCount: { $var: "room.itemCount" },
                                      roomHasVideo: { $var: "room.hasVideo" },
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      ],
                    },
                    children: [
                      {
                        type: "BulletItem",
                        props: { text: { $var: "item.label" } },
                        repeat: { for: { $var: "room.items" }, as: "item" },
                      },
                      {
                        type: "Text",
                        props: { text: "아직 기록되지 않음", tone: "warning" },
                        visibleWhen: { "==": [{ var: "room.itemCount" }, 0] },
                      },
                    ],
                  },
                ],
              },
            ],
          },

          // ── 5) 추가공간 ───────────────────────────────────────────────────
          {
            type: "Card",
            props: { icon: "📦", title: "추가공간" },
            slots: {
              trailing: [
                {
                  type: "Tag",
                  props: {
                    label: "{{additionalDone}}/{{additionalTotal}} 완료",
                    variant: { $var: "additionalVariant" },
                  },
                  visibleWhen: { ">": [{ var: "additionalTotal" }, 0] },
                },
              ],
            },
            children: [
              {
                type: "Group",
                props: { padded: true },
                children: [
                  {
                    type: "ListRow",
                    props: { label: { $var: "space.label" } },
                    repeat: {
                      for: { $var: "additionalSpaces" },
                      as: "space",
                      key: "id",
                    },
                    slots: {
                      trailing: [
                        {
                          type: "Group",
                          props: { direction: "row" },
                          children: [
                            {
                              type: "Tag",
                              props: { label: "영상", variant: "success" },
                              visibleWhen: {
                                "==": [{ var: "space.hasVideo" }, true],
                              },
                            },
                            {
                              type: "Tag",
                              props: { label: "미촬영", variant: "warn" },
                              visibleWhen: {
                                "!=": [{ var: "space.hasVideo" }, true],
                              },
                            },
                            {
                              type: "Tag",
                              props: { label: "완료", variant: "success" },
                              visibleWhen: {
                                "==": [{ var: "space.hasVideo" }, true],
                              },
                            },
                            {
                              type: "Tag",
                              props: { label: "미완료", variant: "warn" },
                              visibleWhen: {
                                "!=": [{ var: "space.hasVideo" }, true],
                              },
                            },
                          ],
                        },
                      ],
                    },
                  },
                  {
                    type: "Text",
                    props: { text: "추가공간이 없어요.", tone: "muted" },
                    visibleWhen: { isEmpty: { var: "additionalSpaces" } },
                  },
                ],
              },
            ],
          },

          // ── 6) 고객 요청사항 ──────────────────────────────────────────────
          {
            type: "Card",
            props: { icon: "💬", title: "고객 요청사항" },
            slots: {
              trailing: [
                {
                  type: "Tag",
                  props: { label: "{{requestCount}}건", variant: "neutral" },
                  visibleWhen: { ">": [{ var: "requestCount" }, 0] },
                },
                {
                  type: "Tag",
                  props: { label: "미입력", variant: "warn" },
                  visibleWhen: { "==": [{ var: "requestCount" }, 0] },
                },
              ],
            },
            children: [
              {
                type: "Group",
                props: { padded: true, tight: true },
                children: [
                  {
                    type: "Text",
                    props: {
                      text: "요청사항이 아직 기록되지 않았어요.",
                      tone: "muted",
                    },
                    visibleWhen: { "==": [{ var: "requestCount" }, 0] },
                  },

                  {
                    type: "FieldLabel",
                    props: { text: "일반 요청", note: "{{requests.length}}건" },
                    visibleWhen: { not: { isEmpty: { var: "requests" } } },
                  },
                  {
                    type: "BulletItem",
                    props: { text: { $var: "request" }, tone: "filled" },
                    repeat: { for: { $var: "requests" }, as: "request" },
                  },

                  {
                    type: "FieldLabel",
                    props: {
                      text: "유료 견적 요청",
                      note: "{{paidRequests.length}}건",
                    },
                    visibleWhen: { not: { isEmpty: { var: "paidRequests" } } },
                  },
                  {
                    type: "BulletItem",
                    props: { text: { $var: "paid" }, tone: "paid" },
                    repeat: { for: { $var: "paidRequests" }, as: "paid" },
                  },
                ],
              },
            ],
          },
        ],
      },
      next: { kind: "exit" },
    },
  },

  /**
   * 방 상세 수정 시트.
   *
   * 프로덕션에서 "수정" 을 누르면 뜨는 시트에 해당한다. 시트도 자산 트리이므로 카드·행·
   * 태그 같은 같은 어휘를 그대로 쓴다 — 시트라고 별도의 UI 규칙을 두지 않는다.
   */
  sheets: {
    roomDetail: {
      title: "방 기록 수정",
      variant: "slide",
      layout: {
        type: "Group",
        props: { padded: true, tight: true },
        children: [
          { type: "Text", props: { text: "{{roomLabel}}", strong: true } },
          {
            type: "InfoRow",
            props: { label: "기록된 짐", value: "{{roomItemCount}}개" },
          },
          {
            type: "InfoRow",
            props: { label: "영상", value: "촬영 완료" },
            visibleWhen: { "==": [{ var: "roomHasVideo" }, true] },
          },
          {
            type: "InfoRow",
            props: { label: "영상", value: null, emptyText: "미촬영" },
            visibleWhen: { "!=": [{ var: "roomHasVideo" }, true] },
          },
          {
            type: "CtaButton",
            props: { label: "닫기", variant: "tertiary" },
            on: { onPress: [{ do: "closeSheet" }] },
          },
        ],
      },
    },
  },
};

/** CounselReviewScreen 이 실제로 받는 값과 같은 모양의 표본. */
const initialValues: Record<string, unknown> = {
  proceeding: false,
  movingDate: "2026년 8월 14일 (금)",
  movingTypeSummary: "가정 이사 · 아파트 → 아파트",
  departure: {
    address: "서울시 강남구 테헤란로 123 미소빌딩 101동 1502호",
    movingMethod: "엘리베이터",
  },
  arrival: {
    address: "경기도 성남시 분당구 판교역로 235",
    supplyArea: null,
    movingMethod: "사다리차",
  },
  packing: "반포장",
  roomsDone: 2,
  roomsTotal: 4,
  roomsAllDoneVariant: "warn",
  rooms: [
    {
      id: "room-1",
      label: "안방",
      hasVideo: true,
      itemCount: 2,
      items: [{ label: "침대 (퀸)" }, { label: "옷장 (3문)" }],
    },
    {
      id: "room-2",
      label: "작은방",
      hasVideo: true,
      itemCount: 1,
      items: [{ label: "책상" }],
    },
    { id: "room-3", label: "거실", hasVideo: false, itemCount: 0, items: [] },
    { id: "room-4", label: "주방", hasVideo: false, itemCount: 0, items: [] },
  ],
  additionalDone: 1,
  additionalTotal: 2,
  additionalVariant: "warn",
  additionalSpaces: [
    { id: "space-1", label: "베란다", hasVideo: true },
    { id: "space-2", label: "세대창고", hasVideo: false },
  ],
  requestCount: 3,
  requests: ["피아노가 있어 사다리차가 필요합니다.", "오전 9시 이전 도착 희망"],
  paidRequests: ["에어컨 분리·설치 견적 요청"],
};

export const COUNSEL_REVIEW = { doc, initialValues };
